import { Phone, Mail, MessageSquare, MapPin, Clock } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { QuoteForm } from "@/components/contact/QuoteForm";
import { useSeo } from "@/components/site/Seo";
import { BUSINESS } from "@/config/businessConfig";

const METHODS = [
  { icon: Phone, label: "Call", value: BUSINESS.phoneDisplay, href: BUSINESS.phoneHref, id: "call" },
  { icon: MessageSquare, label: "Text", value: BUSINESS.phoneDisplay, href: BUSINESS.smsHref, id: "text" },
  { icon: Mail, label: "Email", value: BUSINESS.email, href: BUSINESS.emailHref, id: "email" },
];

const Sidebar = () => (
  <div className="space-y-4">
    {METHODS.map((m) => (
      <a key={m.id} href={m.href} data-testid={`contact-${m.id}-link`}
        className="group flex items-center gap-5 border border-[var(--line)] bg-[var(--char)] p-5 hover:border-[var(--orange)] transition-colors duration-300">
        <span className="w-12 h-12 grid place-items-center bg-[var(--orange)] text-[var(--ink)] shrink-0 transition-transform duration-500 group-hover:rotate-[-8deg]"><m.icon className="w-5 h-5" /></span>
        <span className="min-w-0">
          <span className="block font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-400">{m.label}</span>
          <span className="block font-display text-2xl font-bold break-all">{m.value}</span>
        </span>
      </a>
    ))}
    <div className="border border-[var(--line)] bg-[var(--char)] p-6" data-testid="contact-hours">
      <p className="flex items-center gap-2 font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-400 mb-4"><Clock className="w-4 h-4 text-[var(--orange)]" />Hours</p>
      {BUSINESS.hours.map(([d, h]) => (
        <div key={d} className="flex justify-between py-2 border-b border-[var(--line)] last:border-0 text-sm"><span className="text-zinc-300">{d}</span><span className="text-zinc-200">{h}</span></div>
      ))}
    </div>
    <div className="border border-[var(--line)] bg-[var(--char)] p-6">
      <p className="flex items-center gap-2 font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-400 mb-3"><MapPin className="w-4 h-4 text-[var(--orange)]" />Service area</p>
      <p className="text-zinc-300 text-sm leading-relaxed">Based in {BUSINESS.baseCity}. Serving Woodstock, Ingersoll and most of Oxford County.</p>
    </div>
  </div>
);

export default function Contact() {
  useSeo({
    title: "Request a Quote | Assemble Pro – Woodstock, Ingersoll & Oxford County",
    description: "Request a free assembly or installation quote in Woodstock, Ingersoll or Oxford County. Send job details and photos online, or call or email Assemble Pro.",
    path: "/contact",
  });
  return (
    <div data-testid="contact-page">
      <PageHero testId="contact-hero" eyebrow="Contact · Request a quote"
        lines={[{ text: "Request a" }, { text: "Free Quote.", className: "text-[var(--orange)]" }]}
        intro="Tell us what you bought and where it's going. Photos help us quote faster and more accurately. Prefer to talk? Call, text or email." />
      <section className="bg-[var(--ink)] pb-24 lg:pb-36">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-8 order-1">
            <div className="relative border border-[var(--line)] bg-[var(--char)] p-6 sm:p-10">
              <div className="hazard absolute top-0 left-0 right-0 h-1.5" />
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl mb-8">Job details</h2>
              <QuoteForm />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4 order-2" delay={0.1}><Sidebar /></Reveal>
        </div>
      </section>
    </div>
  );
}
